import React, { useState, useRef, useEffect } from 'react'
import ReactMarkdown from 'react-markdown'
import rehypeHighlight from 'rehype-highlight'
import remarkGfm from 'remark-gfm'
import 'highlight.js/styles/github.css'
import '@/styles/highlight-dark.css'
import { OpsMessage } from '@/store/slices/opsChatSlice'
import { X } from 'lucide-react'
import ChartComponent from './Chart'
import HierarchicalTable from './HierarchicalTable'
import { FeedbackButton } from '@/components/common/FeedbackButton'
import { feedbackService } from '@/services/feedbackService'
import { useToast } from '@/hooks/useToast'

interface StreamingOpsMessageProps {
  message: OpsMessage
  onCancelStream?: (messageId: string) => void
  // PowerBI data display removed - queries are recorded for auditing only
}

export const StreamingOpsMessage: React.FC<StreamingOpsMessageProps> = ({
  message,
  onCancelStream
}) => {
  const [isTyping, setIsTyping] = useState(true)
  const contentRef = useRef<HTMLDivElement>(null)
  const hasAnimated = useRef(false)
  const [currentFeedback, setCurrentFeedback] = useState<{ rating: number; comment: string | null } | null>(null)
  const [feedbackLoading, setFeedbackLoading] = useState(false)
  const { toast } = useToast()

  // Handle typing animation
  useEffect(() => {
    if (message.isStreaming && !hasAnimated.current) {
      setIsTyping(true)
      hasAnimated.current = true
      
      const timer = setTimeout(() => {
        setIsTyping(false)
      }, 800)
      
      return () => clearTimeout(timer)
    } else if (!message.isStreaming && hasAnimated.current) {
      setIsTyping(false)
      hasAnimated.current = false
    }
  }, [message.isStreaming, message.content, message.id, isTyping])

  // Auto-scroll for streaming content
  useEffect(() => {
    if (message.isStreaming && contentRef.current) {
      contentRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [message.content, message.isStreaming])

  // Load existing feedback for this message
  useEffect(() => {
    const loadFeedback = async () => {
      // Only load feedback for completed assistant messages
      if (message.role !== 'ASSISTANT' || message.isStreaming) return
      
      try {
        setFeedbackLoading(true)
        const feedback = await feedbackService.getMessageFeedback(message.id)
        setCurrentFeedback(feedback)
      } catch (error) {
        console.error('Failed to load feedback:', error)
      } finally {
        setFeedbackLoading(false)
      }
    }

    loadFeedback()
  }, [message.id, message.role, message.isStreaming])

  const handleSubmitFeedback = async (rating: number, comment?: string) => {
    try {
      const feedback = await feedbackService.submitFeedback({
        messageId: message.id,
        rating,
        comment
      })
      
      setCurrentFeedback({
        rating: feedback.rating,
        comment: feedback.comment
      })
      
      toast.success('Feedback submitted', 'Thank you for your feedback! It helps us improve our responses.')
    } catch (error) {
      console.error('Failed to submit feedback:', error)
      toast.error('Failed to submit feedback', 'Please try again later.')
      throw error // Re-throw so the UI can handle loading states
    }
  }

  // PowerBI queries are recorded in database for auditing but not displayed in UI

  return (
    <div className="bg-muted rounded-lg p-4 max-w-4xl relative group">
      {/* Cancel button for streaming messages */}
      {message.isStreaming && onCancelStream && (
        <button
          onClick={() => onCancelStream(message.id)}
          className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-destructive/90"
          title="Cancel response"
        >
          <X className="w-3 h-3" />
        </button>
      )}

      {/* Error state */}
      {message.error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3 mb-4">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 rounded-full bg-destructive flex-shrink-0"></div>
            <span className="text-sm text-destructive font-medium">Error</span>
          </div>
          <p className="text-sm text-destructive mt-1">{message.error}</p>
        </div>
      )}

      {/* Message content */}
      <div ref={contentRef} className="min-h-[1rem]">
        {message.content ? (
          <div className="prose prose-base max-w-full dark:prose-invert">
            <ReactMarkdown 
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeHighlight]}
              components={{
                p: ({ children }) => <p className="mb-3 last:mb-0 text-base leading-relaxed">{children}</p>,
                ul: ({ children }) => <ul className="mb-3 last:mb-0 ml-6 space-y-1 list-disc list-outside">{children}</ul>,
                ol: ({ children }) => <ol className="mb-3 last:mb-0 ml-6 space-y-1 list-decimal list-outside">{children}</ol>,
                li: ({ children }) => <li className="text-base leading-relaxed">{children}</li>,
                table: ({ children }) => (
                  <div className="overflow-x-auto my-4 rounded-lg border border-gray-300 dark:border-white shadow-sm bg-white dark:bg-black max-w-full">
                    <table className="w-full table-auto">
                      {children}
                    </table>
                  </div>
                ),
                thead: ({ children }) => (
                  <thead className="bg-gray-100 dark:bg-gray-700 border-b-2 border-gray-300 dark:border-white">
                    {children}
                  </thead>
                ),
                tbody: ({ children }) => (
                  <tbody className="bg-white dark:bg-black">
                    {children}
                  </tbody>
                ),
                tr: ({ children }) => (
                  <tr className="border-b border-gray-200 dark:border-white/50 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                    {children}
                  </tr>
                ),
                th: ({ children }) => (
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wide border-r border-gray-300 dark:border-white last:border-r-0">
                    {children}
                  </th>
                ),
                td: ({ children }) => (
                  <td className="px-4 py-2 text-sm text-gray-900 dark:text-white border-r border-gray-300 dark:border-white last:border-r-0">
                    {children}
                  </td>
                ),
                code: ({ children, ...props }) => 
                  <code className="bg-muted px-2 py-1 rounded text-sm font-mono" {...props}>
                    {children}
                  </code>,
                pre: ({ children }) => 
                  <pre className="bg-muted/50 p-4 rounded-md text-sm overflow-auto my-3 font-mono border max-w-full">
                    {children}
                  </pre>,
                h1: ({ children }) => <h1 className="text-xl font-bold mb-3 mt-2">{children}</h1>,
                h2: ({ children }) => <h2 className="text-lg font-bold mb-2 mt-2">{children}</h2>,
                h3: ({ children }) => <h3 className="text-base font-semibold mb-2 mt-2">{children}</h3>,
                blockquote: ({ children }) => 
                  <blockquote className="border-l-4 border-primary pl-4 my-3 italic text-muted-foreground">
                    {children}
                  </blockquote>,
                strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                em: ({ children }) => <em className="italic">{children}</em>,
                hr: () => <hr className="my-4 border-border" />
              }}
            >
              {message.content}
            </ReactMarkdown>
          </div>
        ) : (
          // Show loading state when streaming with no content
          message.isStreaming && (
            <div className="flex items-center space-x-2 py-2">
              {isTyping ? (
                // Show animated dots while typing animation is active
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
                  <div className="w-2 h-2 bg-primary rounded-full animate-pulse" style={{ animationDelay: '0.1s' }}></div>
                  <div className="w-2 h-2 bg-primary rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                </div>
              ) : (
                // Show spinner after typing animation completes
                <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
              )}
              <span className="text-sm text-muted-foreground">
                Processing request...
              </span>
            </div>
          )
        )}

        {/* Streaming cursor */}
        {message.isStreaming && message.content && (
          <span className="inline-block w-2 h-5 bg-primary ml-1 animate-pulse"></span>
        )}
      </div>

      {/* Charts Section */}
      {message.charts && message.charts.length > 0 && (
        <div className="mt-6 space-y-6 w-full">
          <div className="text-sm font-medium text-muted-foreground border-t border-border pt-4 w-full">
            Charts & Visualizations
          </div>
          <div className="w-full space-y-4">
            {message.charts.map((chart, index) => (
              <ChartComponent 
                key={`${message.id}-chart-${index}`}
                chartData={chart}
                className=""
              />
            ))}
          </div>
        </div>
      )}

      {/* Hierarchical Tables Section */}
      {message.hierarchicalTables && message.hierarchicalTables.length > 0 && (
        <div className="mt-6 space-y-6 w-full">
          <div className="text-sm font-medium text-muted-foreground border-t border-border pt-4 w-full">
            Hierarchical Data Tables
          </div>
          <div className="w-full space-y-4">
            {message.hierarchicalTables.map((table, index) => (
              <HierarchicalTable 
                key={`${message.id}-table-${index}`}
                tableData={table}
                className=""
              />
            ))}
          </div>
        </div>
      )}

      {/* PowerBI Data Sources - Hidden for auditing purposes only */}
      {/* PowerBI queries are recorded in the database for auditing but not displayed to users */}

      {/* Feedback buttons for completed assistant messages */}
      {message.role === 'ASSISTANT' && !message.isStreaming && message.content && (
        <div className="mt-4 pt-3 border-t border-border/50">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Was this response helpful?</span>
            <FeedbackButton
              messageId={message.id}
              currentFeedback={currentFeedback}
              onSubmitFeedback={handleSubmitFeedback}
              disabled={feedbackLoading}
              size="sm"
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default StreamingOpsMessage